// src/pages/home/HomePage.js
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import Button from "../../components/common/Button";
import { getActiveNotices } from "../../api/noticeApi";
import { getReviewLimitsToday } from "../../api/reviewApi";
import { getProfile } from "../../api/userApi";
import { getFileUrl } from "../../utils/fileUrl";
import { FiChevronLeft, FiChevronRight, FiTrendingUp, FiAward, FiDollarSign } from "react-icons/fi";
import { MdRestaurant, MdHotel, MdFitnessCenter } from "react-icons/md";

const CATEGORY_CONFIG = [
  {
    key: "RESTAURANT",
    label: "Restaurant Reviews",
    description: "Review restaurants and earn per task.",
    icon: MdRestaurant,
    gradient: "from-orange-500 to-red-600",
    bgGradient: "from-orange-50 to-red-50",
  },
  {
    key: "HOTEL",
    label: "Hotel Reviews",
    description: "Review hotels (Silver and above).",
    icon: MdHotel,
    gradient: "from-blue-500 to-indigo-600",
    bgGradient: "from-blue-50 to-indigo-50",
  },
  {
    key: "GYM",
    label: "Gym Reviews",
    description: "Review gyms (Gold only).",
    icon: MdFitnessCenter,
    gradient: "from-green-500 to-teal-600",
    bgGradient: "from-green-50 to-teal-50",
  },
];

function HomePage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const userId = user.user_id

  const [notices, setNotices] = useState([]);
  const [currentNoticeIndex, setCurrentNoticeIndex] = useState(0);

  const [limits, setLimits] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [limitError, setLimitError] = useState("");

  // Load notices + limits + profile
  useEffect(() => {
    async function load() {
      try {
        setLoading(true);
        setLimitError("");
        const noticeData = await getActiveNotices()
        const limitData = await getReviewLimitsToday(userId)
        const profileData = await getProfile(userId)

        setNotices(noticeData || []);
        setLimits(limitData);
        setProfile(profileData);
        setCurrentNoticeIndex(0);
      } catch (err) {
        console.error(err);
        setLimitError("Failed to load data.");
      } finally {
        setLoading(false);
      }
    }

    load();
  }, [userId]);

  // Auto-slide notices
  useEffect(() => {
    if (!notices || notices.length <= 1) return;

    const intervalId = setInterval(() => {
      setCurrentNoticeIndex((prev) =>
        prev + 1 >= notices.length ? 0 : prev + 1
      );
    }, 5000); // 5 seconds

    return () => clearInterval(intervalId);
  }, [notices]);

  const handlePrevNotice = () => {
    if (!notices.length) return;
    setCurrentNoticeIndex((prev) =>
      prev - 1 < 0 ? notices.length - 1 : prev - 1
    );
  };

  const handleNextNotice = () => {
    if (!notices.length) return;
    setCurrentNoticeIndex((prev) =>
      prev + 1 >= notices.length ? 0 : prev + 1
    );
  };

  const handleStartTask = (categoryKey) => {
    if (!limits) return;

    if (!limits.can_review) {
      setLimitError(
        "If you want to start working/income/reviewing, please activate your account first."
      );
      return;
    }

    const catInfo = limits.categories?.[categoryKey];

    if (!catInfo || !catInfo.allowed) {
      setLimitError("Your current account level cannot review this category.");
      return;
    }

    if (limits.remaining_total <= 0) {
      setLimitError(
        "Your daily review limit is over. Please come back tomorrow."
      );
      return;
    }

    if (catInfo.remaining <= 0) {
      setLimitError(
        `Your daily ${categoryKey.toLowerCase()} review limit is over.`
      );
      return;
    }

    navigate(`/review/${categoryKey.toLowerCase()}`);
  };

  const renderNoticeSlider = () => {
    if (!notices || notices.length === 0) {
      return (
        <div className="relative w-full aspect-[16/6] md:aspect-[21/6] rounded-2xl overflow-hidden bg-gradient-to-br from-primary-600 via-primary-700 to-secondary-600 flex items-center justify-center shadow-glow">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZGVmcz48cGF0dGVybiBpZD0iZ3JpZCIgd2lkdGg9IjQwIiBoZWlnaHQ9IjQwIiBwYXR0ZXJuVW5pdHM9InVzZXJTcGFjZU9uVXNlIj48cGF0aCBkPSJNIDQwIDAgTCAwIDAgMCA0MCIgZmlsbD0ibm9uZSIgc3Ryb2tlPSJ3aGl0ZSIgc3Ryb2tlLW9wYWNpdHk9IjAuMSIgc3Ryb2tlLXdpZHRoPSIxIi8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2dyaWQpIi8+PC9zdmc+')] opacity-20"></div>
          <div className="relative text-center px-6 py-8 animate-fadeIn max-w-4xl mx-auto">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-white/10 backdrop-blur-sm mb-6">
              <FiTrendingUp className="h-10 w-10 text-white" />
            </div>
            <h2 className="text-white font-bold text-3xl md:text-5xl mb-4 drop-shadow-lg">
              Welcome to Review Market EU!
            </h2>
            <p className="text-primary-100 text-lg md:text-xl max-w-2xl mx-auto leading-relaxed">
              Share Your Opinion, Get Paid! Start reviewing restaurants, hotels, and gyms today.
            </p>
          </div>
        </div>
      );
    }

    const notice = notices[currentNoticeIndex];

    return (
      <div className="relative w-full aspect-[16/6] md:aspect-[21/6] rounded-2xl overflow-hidden shadow-glow group">
        {notice.url ? (
          <img
            src={getFileUrl(notice.url)}
            alt={notice.text || "Notice"}
            className="h-full w-full object-contain bg-gradient-to-br from-gray-900 to-gray-800 group-hover:scale-105 transition-transform duration-700"
          />
        ) : (
          <div className="h-full w-full bg-gradient-to-br from-primary-600 via-purple-600 to-secondary-600">
            <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZGVmcz48cGF0dGVybiBpZD0iZ3JpZCIgd2lkdGg9IjQwIiBoZWlnaHQ9IjQwIiBwYXR0ZXJuVW5pdHM9InVzZXJTcGFjZU9uVXNlIj48cGF0aCBkPSJNIDQwIDAgTCAwIDAgMCA0MCIgZmlsbD0ibm9uZSIgc3Ryb2tlPSJ3aGl0ZSIgc3Ryb2tlLW9wYWNpdHk9IjAuMSIgc3Ryb2tlLXdpZHRoPSIxIi8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2dyaWQpIi8+PC9zdmc+')] opacity-20"></div>
          </div>
        )}

        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />

        {/* <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10 text-white">
          <div className="max-w-4xl">
            <div className="font-bold text-2xl md:text-4xl mb-2 drop-shadow-2xl leading-tight">
              {notice.text || "Notice"}
            </div>
          </div>
        </div> */}

        {notices.length > 1 && (
          <>
            <button
              type="button"
              onClick={handlePrevNotice}
              className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 inline-flex h-12 w-12 items-center justify-center rounded-full bg-white/20 backdrop-blur-md text-white hover:bg-white/40 transition-all shadow-lg hover:scale-110"
            >
              <FiChevronLeft className="h-7 w-7" />
            </button>
            <button
              type="button"
              onClick={handleNextNotice}
              className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 inline-flex h-12 w-12 items-center justify-center rounded-full bg-white/20 backdrop-blur-md text-white hover:bg-white/40 transition-all shadow-lg hover:scale-110"
            >
              <FiChevronRight className="h-7 w-7" />
            </button>
          </>
        )}

        {notices.length > 1 && (
          <div className="absolute bottom-6 left-0 right-0 flex justify-center gap-2">
            {notices.map((_, idx) => (
              <button
                key={idx}
                type="button"
                onClick={() => setCurrentNoticeIndex(idx)}
                className={`h-2 rounded-full transition-all shadow-md ${
                  idx === currentNoticeIndex
                    ? "w-10 bg-white"
                    : "w-2 bg-white/60 hover:bg-white/80 hover:w-4"
                }`}
              />
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderLevelProgress = () => {
    if (!profile) return null;

    const currentStatus = profile.status_level || "NEW";

    // Define status hierarchy
    const statusHierarchy = ["NEW", "ACTIVATED", "BRONZE", "SILVER", "GOLD"];
    const currentStatusIndex = statusHierarchy.indexOf(currentStatus);

    const levelTiers = [
      {
        name: "New Account",
        key: "NEW",
        gradient: "from-red-500 to-red-600",
        bgGradient: "from-red-50 to-rose-50",
        borderColor: "border-red-400",
        icon: FiAward,
        iconColor: "text-red-700",
        benefits: ["Everything is locked", "Activate to unlock"]
      },
      {
        name: "Activated Account",
        key: "ACTIVATED",
        gradient: "from-green-500 to-green-600",
        bgGradient: "from-green-50 to-emerald-50",
        borderColor: "border-green-400",
        icon: FiAward,
        iconColor: "text-green-700",
        benefits: ["Can earn money", "Restaurant review only", "Cannot withdraw money"]
      },
      {
        name: "Bronze Account",
        key: "BRONZE",
        gradient: "from-orange-600 via-amber-600 to-orange-700",
        bgGradient: "from-orange-50 to-amber-50",
        borderColor: "border-orange-400",
        icon: FiAward,
        iconColor: "text-orange-700",
        benefits: ["Can earn money", "Can withdraw money", "Restaurant review only"]
      },
      {
        name: "Silver Account",
        key: "SILVER",
        gradient: "from-gray-400 via-gray-500 to-gray-600",
        bgGradient: "from-gray-100 to-gray-200",
        borderColor: "border-gray-400",
        icon: FiTrendingUp,
        iconColor: "text-gray-700",
        benefits: ["All Bronze benefits", "Hotel Reviews", "Daily Limit: 10"]
      },
      {
        name: "Gold Account",
        key: "GOLD",
        gradient: "from-yellow-400 via-yellow-500 to-yellow-600",
        bgGradient: "from-yellow-50 to-yellow-100",
        borderColor: "border-yellow-500",
        icon: FiDollarSign,
        iconColor: "text-yellow-700",
        benefits: ["All Silver benefits", "Gym Reviews", "Daily Limit: 15", "Premium Support"]
      }
    ];

    return (
      <div className="mb-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
          <FiAward className="h-6 w-6 text-primary-600" />
          Account Level Progress
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
          {levelTiers.map((tier, index) => {
            const Icon = tier.icon;
            const isActive = currentStatus === tier.key;
            const tierStatusIndex = statusHierarchy.indexOf(tier.key);
            const isUnlocked = tierStatusIndex <= currentStatusIndex;
            const isLocked = tierStatusIndex > currentStatusIndex;

            return (
              <div
                key={tier.key}
                className={`relative overflow-hidden rounded-2xl border-3 ${tier.borderColor} transition-all duration-300 ${
                  isActive ? "shadow-glow scale-105" : "shadow-card hover:shadow-soft"
                } ${isLocked ? "opacity-75" : ""}`}
              >
                {/* Gradient Header */}
                <div className={`bg-gradient-to-r ${tier.gradient} p-6 text-white relative`}>
                  <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZGVmcz48cGF0dGVybiBpZD0iZ3JpZCIgd2lkdGg9IjQwIiBoZWlnaHQ9IjQwIiBwYXR0ZXJuVW5pdHM9InVzZXJTcGFjZU9uVXNlIj48cGF0aCBkPSJNIDQwIDAgTCAwIDAgMCA0MCIgZmlsbD0ibm9uZSIgc3Ryb2tlPSJ3aGl0ZSIgc3Ryb2tlLW9wYWNpdHk9IjAuMSIgc3Ryb2tlLXdpZHRoPSIxIi8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2dyaWQpIi8+PC9zdmc+')] opacity-20"></div>

                  <div className="relative flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="p-3 bg-white/20 backdrop-blur-md rounded-xl">
                        <Icon className="h-8 w-8" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold">{tier.name}</h3>
                        <p className="text-xs text-white/80">Level {index + 1}</p>
                      </div>
                    </div>
                    {isActive && (
                      <div className="px-3 py-1.5 bg-white/20 backdrop-blur-md rounded-full text-xs font-bold border-2 border-white/40 animate-pulse">
                        ACTIVE
                      </div>
                    )}
                  </div>
                </div>

                {/* Benefits Section */}
                <div className={`bg-gradient-to-br ${tier.bgGradient} p-6`}>
                  <div className="space-y-2.5">
                    {tier.benefits.map((benefit, idx) => (
                      <div key={idx} className="flex items-start gap-2">
                        <svg className={`h-5 w-5 ${tier.iconColor} flex-shrink-0 mt-0.5`} fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span className={`text-sm font-medium ${tier.iconColor}`}>{benefit}</span>
                      </div>
                    ))}
                  </div>

                  {isLocked && (
                    <div className="mt-4 pt-4 border-t-2 border-white/60">
                      <button className="w-full py-2.5 px-4 bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 text-white font-semibold rounded-lg shadow-md hover:shadow-lg transition-all">
                        Upgrade to {tier.name}
                      </button>
                    </div>
                  )}
                </div>

                {/* Active Indicator */}
                {isActive && (
                  <div className="absolute top-0 right-0 w-24 h-24 overflow-hidden">
                    <div className="absolute top-4 right-[-32px] rotate-45 bg-white/30 backdrop-blur-md text-white text-xs font-bold py-1 px-10 text-center">
                      CURRENT
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderLimitsSummary = () => {
    if (!limits || !limits.can_review) {
      return (
        <div className="card bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200">
          <div className="flex items-start gap-3">
            <FiAward className="h-6 w-6 text-amber-600 flex-shrink-0" />
            <div>
              <p className="font-semibold text-amber-900 mb-1">Account Activation Required</p>
              <p className="text-sm text-amber-700">
                Your account is <span className="font-bold">New</span>. Please activate your account to start reviewing and earning.
              </p>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="card bg-gradient-to-br from-primary-50 to-secondary-50 border-primary-200">
        <div className="flex items-center gap-2 mb-3">
          <FiDollarSign className="h-5 w-5 text-primary-600" />
          <h3 className="font-semibold text-gray-900">Today's Review Limits</h3>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white/60 rounded-lg p-3">
            <p className="text-xs text-gray-600 mb-1">Used Today</p>
            <p className="text-2xl font-bold text-primary-600">{limits.used_today}</p>
            <p className="text-xs text-gray-500">of {limits.daily_limit}</p>
          </div>
          <div className="bg-white/60 rounded-lg p-3">
            <p className="text-xs text-gray-600 mb-1">Remaining</p>
            <p className="text-2xl font-bold text-accent-600">{limits.remaining_total}</p>
            <p className="text-xs text-gray-500">tasks left</p>
          </div>
          {Object.entries(limits.categories || {}).map(([cat, info]) =>
            info.allowed ? (
              <div key={cat} className="bg-white/60 rounded-lg p-3">
                <p className="text-xs text-gray-600 mb-1">{cat}</p>
                <p className="text-xl font-bold text-gray-800">{info.used}/{info.limit}</p>
              </div>
            ) : null
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Page title */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Dashboard</h1>
        <p className="text-gray-600">
          Welcome back! Start reviewing and earning today.
        </p>
      </div>

      {/* Hero notices slider */}
      {renderNoticeSlider()}

      {/* Level Progress Section */}
      {renderLevelProgress()}

      {/* Limits info */}
      {renderLimitsSummary()}

      {limitError && (
        <div className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700 border border-red-200 flex items-start gap-2 animate-fadeIn">
          <svg className="h-5 w-5 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
          <span>{limitError}</span>
        </div>
      )}

      {/* Category cards */}
      <div>
        <h2 className="text-xl font-bold text-gray-900 mb-4">Review Categories</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {CATEGORY_CONFIG.map((cat) => {
            const catLimits = limits?.categories?.[cat.key];
            const allowed = !!catLimits?.allowed && limits?.can_review;
            const disabled =
              !allowed ||
              (limits &&
                (limits.remaining_total <= 0 || catLimits?.remaining <= 0));

            let helper = cat.description;
            if (!limits) {
              // default description
            } else if (!limits.can_review) {
              helper =
                "Activate your account to start reviewing and earning from this category.";
            } else if (!catLimits?.allowed) {
              helper = "Upgrade your account level to unlock this category.";
            } else if (catLimits.remaining <= 0) {
              helper = "You already completed the maximum tasks for today.";
            }

            const Icon = cat.icon;

            return (
              <div
                key={cat.key}
                className={`card-gradient group hover:shadow-glow transition-all duration-300 ${
                  disabled ? "opacity-60" : ""
                }`}
              >
                <div className={`inline-flex p-3 rounded-xl bg-gradient-to-br ${cat.gradient} mb-4`}>
                  <Icon className="h-8 w-8 text-white" />
                </div>

                <h3 className="text-lg font-bold text-gray-900 mb-2">
                  {cat.label}
                </h3>
                <p className="text-sm text-gray-600 mb-4 min-h-[40px]">{helper}</p>

                <div className="flex items-center justify-between mt-auto pt-4 border-t border-gray-200">
                  {limits && catLimits?.allowed && (
                    <div className="text-sm text-gray-600">
                      <span className="font-semibold text-gray-900">{catLimits.used}</span>/{catLimits.limit} done
                    </div>
                  )}
                  <Button
                    size="sm"
                    className="ml-auto"
                    disabled={disabled}
                    onClick={() => handleStartTask(cat.key)}
                    variant={disabled ? "outline" : "primary"}
                  >
                    {disabled ? "Locked" : "Start Task"}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default HomePage;
